import type { ReactNode } from 'react';
import { t, useT } from '@hana/i18n';
import './common.i18n';
import { Button, StateView, useToast } from '@hana/ui';
import { IconId, IconLock, IconPlayerPlay, IconSend } from '@tabler/icons-react';
import s from './mee.module.css';

// Board `Mint-Mee` (const EM + msg): chữ & nút của trạng thái trống / lỗi cho từng tab.
export type MeeTab = 'time' | 'leave' | 'pay' | 'docs' | 'grow' | 'appr';

const EMPTY: Record<MeeTab, [string, string, string]> = {
  time: ['Chưa có chấm công tháng này', 'Chấm công bằng passkey trên điện thoại hoặc máy này.', 'Chấm công vào'],
  leave: ['Chưa có đơn nào', 'Đơn nghỉ phép, tăng ca, công tác, giải trình công của bạn sẽ hiện ở đây.', 'Tạo đơn'],
  pay: ['Chưa có phiếu lương', 'Phiếu hiện sau khi HR duyệt bảng lương kỳ đầu tiên.', 'Xem chấm công'],
  docs: ['Chưa có giấy tờ', 'Thêm bản scan chứng chỉ, khám sức khoẻ, thẻ BHYT để được nhắc gia hạn.', 'Thêm giấy tờ'],
  grow: ['Chưa được giao khoá học', 'Chưa có khoá bắt buộc.', 'Mở HANA Edu'],
  appr: ['Không có đơn chờ duyệt', 'Đơn của nhóm gửi tới ghế của bạn sẽ hiện ở đây.', 'Về Tổng quan'],
};

export function meeStateProps(
  tab: MeeTab,
  state: string,
  retry: () => void,
  onEmpty: () => void,
): { title?: string; desc?: string; icon?: ReactNode; action?: ReactNode } {
  if (state === 'empty') {
    const [title, desc, btn] = EMPTY[tab];
    return { title, desc, icon: <IconId size={26} stroke={1.8} />, action: <Button variant="primary" onClick={onEmpty}>{btn}</Button> };
  }
  if (state === 'error') {
    return {
      title: t('mc.err'),
      desc: t('mc.errDesc'),
      action: <Button icon={<IconPlayerPlay size={16} />} onClick={retry}>{t('mc.retry')}</Button>,
    };
  }
  return {};
}

const LOCK_TILES = ['Lương 09/2026', 'Phép năm còn', 'Công tháng'];

/** Board `Mint-Mee` (isNoperm): chưa liên kết hồ sơ nhân viên — 3 ô tiền che + khung "Gửi yêu cầu tới HR"; dùng chung mọi tab. */
export function NopermView() {
  const t = useT();
  const toast = useToast();
  return (
    <div className={s.lockWrap}>
      <div className={s.lockTiles}>
        {LOCK_TILES.map((k) => (
          <div key={k} className={s.tile}>
            <span>{k}</span>
            <b>••••••<IconLock size={16} aria-hidden="true" /></b>
          </div>
        ))}
      </div>
      <div className={s.stateCard}>
      <StateView
        state="noperm"
        title={t('mc.noLink')}
        desc={t('mc.noLinkDesc')}
        action={<Button icon={<IconSend size={16} />} onClick={() => toast({ msg: t('mc.reqSent') })}>{t('mc.reqHr')}</Button>}
      />
      </div>
    </div>
  );
}

/** Board `Mint-Mee`: trống / lỗi nằm trong một thẻ trắng (ready thì không bọc); noperm = khung chung `NopermView`. */
export function StateCard({ state, children }: { state: string; children: ReactNode }) {
  if (state === 'noperm') return <NopermView />;
  return state === 'empty' || state === 'error' ? <div className={s.stateCard}>{children}</div> : <>{children}</>;
}

/** Bộ khung chờ của board `Mint-Mee` (state=loading): 4 thẻ số + 1 thẻ danh sách 6 dòng. */
export function OverviewSkeleton() {
  return (
    <div className={s.ovSkel} aria-busy="true">
      <div className={s.ovSkelKpis}>
        {[0, 1, 2, 3].map((k) => (
          <div key={k} className={s.ovSkelCard}>
            <i style={{ width: '50%', height: 12 }} />
            <i style={{ width: '35%', height: 26, marginTop: 14, borderRadius: 6 }} />
            <i style={{ width: '80%', height: 10, marginTop: 16 }} />
          </div>
        ))}
      </div>
      <div className={`${s.ovSkelCard} ${s.ovSkelList}`}>
        {[0, 1, 2, 3, 4, 5].map((k) => (
          <div key={k} className={s.ovSkelRow}>
            <i style={{ width: 40, height: 40, borderRadius: 12 }} />
            <div>
              <i style={{ width: '40%', height: 12 }} />
              <i style={{ width: '70%', height: 8, marginTop: 8 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
